import { useCallback, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'apps/shared/scripts/components';
import {
  useIsAuthenticated,
  useYupValidation,
} from 'apps/shared/scripts/hooks';

import CardAuth from '../CardAuth';

import { DEFAULT_VALUES } from './constants';
import GuestBanner from './GuestBanner';
import MediaField from './MediaField';
import MessageField from './MessageField';
import messages from './messages.json';
import NameField from './NameField';
import { FormValues } from './types';
import { schema } from './validation';

export interface Props {
  closeModal: () => void;
  defaultValues?: FormValues;
  isEditMode?: boolean;
  isSubmitting?: boolean;
  isSuccess?: boolean;
  onSubmit: (data: FormValues) => Promise<void>;
}

const CardPostForm = ({
  closeModal,
  defaultValues,
  isEditMode,
  isSubmitting,
  isSuccess,
  onSubmit,
}: Props) => {
  const {
    control,
    clearErrors,
    formState: { errors, isDirty },
    handleSubmit,
    watch,
  } = useForm<FormValues>({
    defaultValues: { ...DEFAULT_VALUES, ...defaultValues },
    resolver: useYupValidation<FormValues>(schema),
  });
  const [showAuth, setShowAuth] = useState<boolean>(false);
  const [skipAuth, setSkipAuth] = useState<boolean>(false);
  const isAuthenticated = useIsAuthenticated();

  const onShowAuth = useCallback(() => {
    setShowAuth(true);
  }, []);

  const onHideAuth = useCallback(() => {
    setShowAuth(false);
    setSkipAuth(false);
  }, []);

  const onSubmitHandler: SubmitHandler<FormValues> = useCallback(
    async (data) => {
      if (!isAuthenticated && !skipAuth) {
        setShowAuth(true);
      } else {
        onSubmit(data);
      }
    },
    [isAuthenticated, onSubmit, skipAuth],
  );

  const onCancel = useCallback(() => {
    if (!isDirty) {
      closeModal();
      setShowAuth(false);
    } else if (
      window.confirm(
        isEditMode
          ? 'Are you sure you want to cancel editing the post?'
          : 'Are you sure you want to cancel adding a post?',
      )
    ) {
      closeModal();
      setShowAuth(false);
    }
  }, [closeModal, isDirty, isEditMode]);

  const onSkipAuth = useCallback(() => {
    setSkipAuth(true);
    setShowAuth(false);
  }, []);

  if (showAuth) {
    return (
      <CardAuth
        onBack={onHideAuth}
        onCancel={onCancel}
        onComplete={onHideAuth}
        onSkip={onSkipAuth}
      />
    );
  }

  return (
    <>
      <ModalHeader
        onBack={skipAuth ? onShowAuth : undefined}
        onDismiss={onCancel}
        title={isEditMode ? messages.editPostTitle : messages.addPostTitle}
      />
      <ModalBody>
        {skipAuth && <GuestBanner />}
        <form onSubmit={handleSubmit(onSubmitHandler)}>
          <NameField control={control} errors={errors} />
          <MessageField control={control} errors={errors} />
          <MediaField
            control={control}
            clearErrors={clearErrors}
            watch={watch}
          />
        </form>
      </ModalBody>
      <ModalFooter
        isDisabled={!isDirty}
        isSubmitting={isSubmitting}
        isSuccess={isSuccess}
        onCancel={onCancel}
        onSubmit={handleSubmit(onSubmitHandler)}
        primaryCtaLabel={
          isEditMode
            ? messages.editPostCtaLabel
            : !isAuthenticated && !skipAuth
              ? messages.continueCtaLabel
              : messages.addPostCtaLabel
        }
      />
    </>
  );
};

CardPostForm.displayName = 'CardPostForm';

export default CardPostForm;
